import { useState } from 'react'
import { NavigateOptions, useLocation } from 'react-router-dom'

import { AuthActionIds } from '@pages/Profile/AccountSettings/SecuritySettings/SecuritySettings.page'
import { TwoFactorAuthWithdrawalWarningModal } from '@pages/TwoFactorAuth/modals/TwoFactorAuthWithdrawalWarning.modal'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { ResponseError, instanceOfAxiosError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isTickmillPartner } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'

import { NameDto } from '@model/NameDto'
import { TwoFAProviderItem } from '@model/TwoFactorAuthentication'

import { useFundingNav } from '@hooks/useFundingNav'

export interface TwoFactorWarningState {
  visible: boolean
  walletId: string | null
}

interface UseWithdrawalPageNavigationProps {
  handleWithdrawalNavigation: (withdrawalOptions?: {
    walletId?: string
    navigateOptions?: NavigateOptions
  }) => Promise<void>
  walletWithdrawLoadingId: string
}

export const useWithdrawalPageNavigation = (): UseWithdrawalPageNavigationProps => {
  const [activeTwoFAProviders, setActiveTwoFAProviders] = useState<TwoFAProviderItem[]>([])
  const [providerTypes, setProviderTypes] = useState<NameDto<number>[]>([])
  const [authTypes, setAuthTypes] = useState<NameDto[]>([])

  const [loadingWalletId, setLoadingWalletId] = useState<string>('')
  const location = useLocation()
  const { navigateToFunding, returnPath } = useFundingNav()
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()

  function getWithdrawalPath(walletId?: string | null): string {
    const isPAWithdrawal = location.pathname.includes('/dashboard/payment-agent/')
    const isIBPath = location.pathname.includes('/dashboard/introducing-broker/')
    const isIBOrPartnerWithdrawal =
      (account?.clientIntroducingBroker?.id || isTickmillPartner(account)) && isIBPath

    if (isPAWithdrawal) {
      return walletId
        ? `${CommonPaths.PaymentAgentWallets}/${walletId}/withdrawal`
        : '/dashboard/payment-agent/withdrawal'
    } else if (isIBOrPartnerWithdrawal) {
      return walletId
        ? `${CommonPaths.IntroducingBrokerWallets}/${walletId}/withdrawal`
        : '/dashboard/introducing-broker/withdrawal'
    } else {
      return walletId
        ? `${CommonPaths.TradersRoomWallets}/${walletId}/withdrawal`
        : `${CommonPaths.TradersRoomWallets}/withdrawal`
    }
  }
  const twoFactorWarningModal = useModal(TwoFactorAuthWithdrawalWarningModal, {
    getWithdrawalPath: getWithdrawalPath,
    withdrawalNavigateOptions: {
      state: { from: 'dashboard', is2FATabSelected: true, returnPath },
    },
  })

  const fetchActive2FAProviders = async (walletId?: string) => {
    try {
      const response = await apiClient.get2FAProviders()
      setActiveTwoFAProviders(response)
      return response
    } catch (error: unknown) {
      if (instanceOfAxiosError((error as ResponseError)?.response)) {
        const errorResponse = (error as ResponseError).response.response?.data
        if (
          'two_factor_authentication_disabled_for_county_of_residence_of_user' ===
          errorResponse?.code
        ) {
          navigateToFunding(
            walletId
              ? `${CommonPaths.TradersRoomWallets}/${walletId}/withdrawal`
              : `${CommonPaths.TradersRoomWallets}/withdrawal/`
          )
        } else {
          throw error
        }
      }
    }
  }

  const fetch2FAProviderTypes = async () => {
    const response = await apiClient.get2FAProviderTypes()
    setProviderTypes(response)
    return response
  }

  const fetch2FAProviderActionTypes = async () => {
    const response = await apiClient.get2FAProviderActionTypes()
    setAuthTypes(response)
    return response
  }

  const handleWithdrawalNavigation = async (withdrawalOptions?: {
    walletId?: string
    navigateOptions?: NavigateOptions
  }) => {
    const { walletId, navigateOptions = { state: {} } } = withdrawalOptions ?? {}
    setLoadingWalletId(walletId ?? 'general')

    try {
      const availableTwoFactorProviderTypes =
        providerTypes.length > 0 ? providerTypes : await fetch2FAProviderTypes()
      const isAny2FAProviderTypeAvailable = !!availableTwoFactorProviderTypes.length
      if (!isAny2FAProviderTypeAvailable) {
        navigateToFunding(getWithdrawalPath(walletId), navigateOptions)
        return
      }

      const availableTwoFactorActionTypes =
        authTypes.length > 0 ? authTypes : await fetch2FAProviderActionTypes()
      const has2FAWithdrawActionTypeAvailable = availableTwoFactorActionTypes.some(
        (actionType) => actionType.id === AuthActionIds.Withdraw
      )
      if (!has2FAWithdrawActionTypeAvailable) {
        navigateToFunding(getWithdrawalPath(walletId), navigateOptions)
        return
      }

      const active2FAProviders =
        activeTwoFAProviders.length > 0
          ? activeTwoFAProviders
          : await fetchActive2FAProviders(walletId)
      const hasWithdrawTwoFactorEnabled =
        active2FAProviders?.[0]?.authActions?.some(({ id }) => id === AuthActionIds.Withdraw) ??
        false

      if (hasWithdrawTwoFactorEnabled) {
        navigateToFunding(getWithdrawalPath(walletId), navigateOptions)
      } else {
        twoFactorWarningModal.open({ walletId })
      }
    } finally {
      setLoadingWalletId('')
    }
  }

  return {
    handleWithdrawalNavigation,
    walletWithdrawLoadingId: loadingWalletId,
  }
}
